import Sketch from "react-p5";
import p5Types from "p5";
import { VideoConverterProps } from "../../interfaces/core";
import { useRef } from "react";
import useState from "react-usestateref";
import { useEffect } from "react";
import { isPlatform, useIonViewDidLeave } from "@ionic/react";
import { chooseArrTpe } from "../../lib/video-converter/logic";

interface ComponentProps {
  settings: VideoConverterProps;
  getCanvas: ({ video, canvas, p5 }: any) => void;
}

const VideoConverterComponent: React.FC<ComponentProps> = ({
  settings,
  getCanvas,
}) => {
  const [_, setVideo, videoRef] = useState(undefined);
  const [stream, setStream, streamRef] = useState(null);
  const p5Ref = useRef<p5Types | undefined>();
  const windowX = Math.min(1400, window.innerWidth);
  const windowY =
    windowX > window.innerHeight
      ? Math.min((windowX * 3) / 4, window.innerHeight - 150)
      : Math.min((windowX * 4) / 3, window.innerHeight - 150);
  const options = {
    video: false,
    audio: {
      // channelCount: 2,
      autoGainControl: false,
      echoCancellation: true,
      noiseSuppression: true,
      sampleRate: 48000,
      sampleSize: 16,
    },
  };
  let switchBtn: p5Types.Element;
  let cameraMode = "environment";
  const startAudio = async () => {
    const audio = await navigator.mediaDevices.getUserMedia(options);
    const audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    const audioSrc = audioCtx.createMediaStreamSource(audio);
    audioSrc.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    setStream({ analyser, data, audio });
  };

  let wave_index = 0;

  const setup = (p5: p5Types, canvasParentRef: Element) => {
    const cnv = p5.createCanvas(windowX, windowY).parent(canvasParentRef);
    p5.frameRate(22);
    cnv.class(
      "rounded-xl shadow-xl shadow-gray-500/10 dark:shadow-2xl dark:shadow-gray-50/10"
    );
    // webcam capture (at the size of the window)
    switchCamera(p5);

    getCanvas({ video: videoRef.current, canvas: cnv, p5 });

    if (isPlatform("mobile") || isPlatform("tablet")) {
      switchBtn = p5.createImg("/assets/icons/camera-reverse-outline.svg");
      switchBtn.parent(document.getElementsByClassName("react-p5")[0]);
      switchBtn.mousePressed(() => switchCamera(p5));
      switchBtn.addClass(
        "rounded-lg shadow-xl shadow-gray-500/10 dark:shadow-2xl dark:shadow-gray-50/10 bg-white/50 hover:bg-white transition filter backdrop-blur-lg z-[10] cursor-pointer w-8 h-8 p-1 bottom-2 right-2 absolute"
      );
    }
    p5Ref.current = p5;
  };

  const draw = (p5: p5Types) => {
    settings.audio?.enabled &&
      streamRef.current?.analyser.getByteFrequencyData(streamRef.current?.data);
    const bg = settings?.display?.background ?? { h: 180, s: 100, l: 100 };
    const audioData = settings.audio?.enabled
      ? streamRef.current?.data && [...streamRef.current?.data]
      : [];
    p5.colorMode("hsl");
    p5.background(bg.h, bg.s, bg.l);

    const influence = settings.audio?.enabled
      ? audioData?.length
        ? audioData[35] / (settings?.display.dpi / 2)
        : 0
      : 0;

    // p5.stroke("purple");
    // p5.strokeCap("round");
    // p5.strokeWeight(10);
    // p5.beginShape();
    // p5.noFill();
    // for (let i = 0; i < audioData?.length; i++) {
    //   const y = p5.map(audioData[i], 0, 255, p5.height, 0);
    //   if (i === 15) {
    //     p5.endShape();
    //     p5.point(i + i * 20, y);
    //     p5.strokeWeight(30);
    //     p5.beginShape();
    //   }
    //   p5.vertex(i + i * 20, y);
    // }
    // p5.endShape();
    let gridSize = Math.round(
      p5.width / (settings?.display?.dpi ? settings.display.dpi : 72)
    );
    gridSize = gridSize > 10 ? gridSize : 10;
    // the video has pixels just like an image!
    // console.log(videoRef.current.width);
    wave_index += 1 / 2;
    if (videoRef.current.width > 0) {
      videoRef.current.loadPixels();
      if (videoRef.current.pixels.length > 0) {
        for (let y = 0; y < videoRef.current.height; y += gridSize) {
          for (let x = 0; x < videoRef.current.width; x += gridSize) {
            // at the current position, get the red
            // value (an approximation for brightness)
            // and use it to create the diameter
            let index = (y * videoRef.current.width + x) * 4;
            let r = videoRef.current.pixels[index];
            // let g = videoRef.current.pixels[index + 1];
            // let b = videoRef.current.pixels[index + 2];
            let diaR = p5.map(r, 0, 255, gridSize, 2) + influence;
            // let diaG = p5.map(g, 0, 255, gridSize * 0.7, 2);
            // let diaB = p5.map(b, 0, 255, gridSize * 0.7, 2);
            // let dia = p5.map((r + g + b) / 3, 0, 255, gridSize, 0);
            //   console.log(r, g, b);
            // draw a circle at the current location
            // using the diameter we calculated
            //   p5.fillSize(diaR);
            //   p5.text(selectNext(reds), x + gridSize / 2 / 3, y + gridSize / 2 / 3);
            //   p5.textSize(diaG);
            //   p5.text(selectNext(greens), x + gridSize / 3, y + gridSize / 3);
            //   p5.textSize(diaB);
            //   p5.text(selectNext(blues), x + gridSize / 2, y + gridSize / 2);
            //   // Random elements => possibleEmoji[Math.floor(Math.random() * possibleEmoji.length)]
            // only draw pixel if showing inside canvas
            if (
              x + gridSize / 2 - (videoRef.current.width - windowX) / 2 > 0 &&
              y + gridSize / 2 - (videoRef.current.height - windowY) / 2
            ) {
              if (settings.pixel.type === "emoji") {
                let selectedArr = chooseArrTpe(
                  settings.pixel.options.source,
                  settings.pixel.options.displayMode,
                  diaR,
                  gridSize,
                  x,
                  y,
                  wave_index
                );
                p5.textSize(diaR);
                p5.text(
                  selectedArr,
                  x + gridSize / 2 - (videoRef.current.width - windowX) / 2,
                  y + gridSize / 2
                );
              } else if (settings.pixel.type === "circle") {
                const fg = settings?.pixel.options.forgroundColor ?? {
                  h: 0,
                  s: 0,
                  l: 0,
                };
                p5.fill(fg.h, fg.s, fg.l);
                p5.noStroke();
                p5.circle(
                  x + gridSize / 2 - (videoRef.current.width - windowX) / 2,
                  y + gridSize / 2 - (videoRef.current.height - windowY) / 2,
                  diaR
                );
              } else if (settings.pixel.type === "character") {
                const fg = settings?.pixel.options.characterColor ?? {
                  h: 0,
                  s: 0,
                  l: 0,
                };
                let selectedArr = chooseArrTpe(
                  settings.pixel.options.character.split(","),
                  settings.pixel.options.displayMode,
                  diaR,
                  gridSize,
                  x,
                  y,
                  wave_index
                );
                p5.fill(fg.h, fg.s, fg.l);
                p5.noStroke();
                p5.textSize(diaR);
                p5.textStyle(p5.BOLD);
                p5.text(
                  selectedArr,
                  x + gridSize / 2 - (videoRef.current.width - windowX) / 2,
                  y + gridSize / 2
                );
              }
            }
          }
        }
      }
    } else {
      console.log("Error: videoRef.current.pixels.length === 0");
    }
  };

  function switchCamera(p5: p5Types) {
    stopCapture();
    videoRef.current && videoRef.current.remove();
    cameraMode = cameraMode === "user" ? "environment" : "user";
    const videoOptions = {
      video: {
        facingMode: cameraMode,
        width: {
          ideal: windowX,
        },
      },
      audio: false,
    };
    const video = p5.createCapture(videoOptions);
    if (video.elt.clientWidth / video.elt.clientHeight < windowX / windowY) {
      video.size(p5.width, "auto");
    } else {
      video.size("auto", p5.height);
    }
    setVideo(video);
    videoRef.current.hide();
  }
  function stopCapture() {
    if (videoRef.current) {
      let stream = videoRef.current.elt.srcObject;
      let tracks = stream.getTracks();

      tracks.forEach(function (track) {
        track.stop();
      });
      videoRef.current.elt.srcObject = null;
    }
  }
  useIonViewDidLeave(() => {
    streamRef.current?.audio &&
      streamRef.current.audio.getTracks().forEach((track) => track.stop());
  });
  useEffect(() => {
    if (settings.audio?.enabled) {
      startAudio();
    }
  }, [settings.audio?.enabled]);
  useEffect(() => {
    return () => {
      videoRef.current.remove();
      p5Ref.current.remove();
    };
  }, []);
  return <Sketch setup={setup} draw={draw} className="react-p5 relative" />;
};
export default VideoConverterComponent;
